import Button from "devextreme-react/button";
import TagBox from "devextreme-react/tag-box";
import React, { useState, useEffect, useMemo } from "react";
import { DefaultPhaseDto } from "../../../dtos/DefaultPhaseDto";
import { WorkflowConfigOptionsDto } from "../../../dtos/WorkflowConfigOptionsDto";
import { AudienceDefinitions } from "../../../dtos/AudienceDefinitions";
import {
    allowedNihRoles,
    allowedGranteeRoles,
    defaultAudiences,
    roleLabels,
} from "../../shared/tasklist/config/audienceConfig";

type Props = {
    task: DefaultPhaseDto;
    configOptions: WorkflowConfigOptionsDto;
    onSave: (data: DefaultPhaseDto) => Promise<void>;
};

export default function TemplateAudiencesTab({ task, configOptions, onSave }: Props) {
    // Filter role options to only show allowed roles for each audience, sorted alphabetically
    const nihRoleOptions = allowedNihRoles.map(role => ({
        id: role,
        name: roleLabels[role] || `Role ${role}`
    })).sort((a, b) => a.name.localeCompare(b.name));

    const granteeRoleOptions = allowedGranteeRoles.map(role => ({
        id: role,
        name: roleLabels[role] || `Role ${role}`
    })).sort((a, b) => a.name.localeCompare(b.name));

    // Get current audiences from task settings
    const getCurrentAudiences = (): AudienceDefinitions => {
        return task?.settings?.audiences ?? { ...defaultAudiences };
    };

    const [audiences, setAudiences] = useState<AudienceDefinitions>(getCurrentAudiences);
    const [savedAudiences, setSavedAudiences] = useState<AudienceDefinitions>(getCurrentAudiences);

    // Reset state when task changes
    useEffect(() => {
        const current = getCurrentAudiences();
        setAudiences(current);
        setSavedAudiences(current);
    }, [task?.id]);

    // Compute dirty state
    const isDirty = useMemo(() => {
        const toComparable = (aud: AudienceDefinitions) => {
            const nihStr = (aud.nih ?? []).slice().sort().join(",");
            const granteeStr = (aud.grantee ?? []).slice().sort().join(",");
            return `nih:[${nihStr}];grantee:[${granteeStr}]`;
        };
        return toComparable(audiences) !== toComparable(savedAudiences);
    }, [audiences, savedAudiences]);

    const handleSave = async () => {
        const updatedTask: DefaultPhaseDto = {
            ...task,
            settings: {
                ...task.settings,
                audiences: audiences,
            }
        };
        await onSave(updatedTask);
        setSavedAudiences(audiences);
    };

    const handleDiscard = () => {
        setAudiences(savedAudiences);
    };

    const handleRestoreDefaults = () => {
        const defaults = configOptions.task.defaultAudiences ?? { nih: [], grantee: [] };
        setAudiences({ nih: defaults.nih ?? [], grantee: defaults.grantee ?? [] });
    };

    const handleClear = () => {
        setAudiences({ nih: [], grantee: [] });
    };

    return (
        <div className="template-tab">
            <div className="template-tab-body">
            <p style={{ marginBottom: "16px", color: "#666" }}>
                Define which roles belong to each audience group. These groups are used for comment visibility
                and notification targeting.
            </p>

            <div style={{ display: "grid", gap: "24px", maxWidth: "600px" }}>
                {/* NIH Audience */}
                <div
                    style={{
                        padding: "16px",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                    }}
                >
                    <div style={{ marginBottom: "12px" }}>
                        <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "4px" }}>
                            NIH Audience
                        </div>
                        <div style={{ fontSize: "12px", color: "#888" }}>
                            Roles that are considered part of the NIH/internal audience.
                            Comments marked as "NIH Only" will only be visible to these roles.
                        </div>
                    </div>
                    <TagBox
                        items={nihRoleOptions}
                        displayExpr="name"
                        valueExpr="id"
                        value={audiences.nih}
                        onValueChanged={(e) => setAudiences(prev => ({ ...prev, nih: e.value ?? [] }))}
                        width="100%"
                        placeholder="Select NIH roles..."
                        showSelectionControls={true}
                    />
                </div>

                {/* Grantee Audience */}
                <div
                    style={{
                        padding: "16px",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                    }}
                >
                    <div style={{ marginBottom: "12px" }}>
                        <div style={{ fontWeight: 600, fontSize: "14px", marginBottom: "4px" }}>
                            Grantee Audience
                        </div>
                        <div style={{ fontSize: "12px", color: "#888" }}>
                            Roles that are considered part of the Grantee/external audience.
                            Comments marked as "Grantee Only" will only be visible to these roles.
                        </div>
                    </div>
                    <TagBox
                        items={granteeRoleOptions}
                        displayExpr="name"
                        valueExpr="id"
                        value={audiences.grantee}
                        onValueChanged={(e) => setAudiences(prev => ({ ...prev, grantee: e.value ?? [] }))}
                        width="100%"
                        placeholder="Select Grantee roles..."
                        showSelectionControls={true}
                    />
                </div>
            </div>

            </div>

            <div className="template-tab-footer">
                <Button
                    text={isDirty ? "Save Audiences *" : "Save Audiences"}
                    type="default"
                    stylingMode="contained"
                    onClick={handleSave}
                />
                {isDirty && (
                    <Button
                        text="Discard Changes"
                        type="normal"
                        stylingMode="outlined"
                        onClick={handleDiscard}
                    />
                )}
                <Button
                    text="Restore Defaults"
                    type="normal"
                    stylingMode="outlined"
                    onClick={handleRestoreDefaults}
                />
                <Button
                    text="Clear All"
                    type="danger"
                    stylingMode="outlined"
                    onClick={handleClear}
                />
                {isDirty && (
                    <span style={{ color: "#f0ad4e", fontSize: "12px", marginLeft: "8px" }}>
                        Unsaved changes
                    </span>
                )}
            </div>
        </div>
    );
}
